import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import RealityCheck from './components/RealityCheck';
import WhatWeDo from './components/WhatWeDo';
import HowItWorks from './components/HowItWorks';
import WhatYouGet from './components/WhatYouGet';
import Testimonials from './components/Testimonials';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

export default function Home() {
  const [showNavbar, setShowNavbar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowNavbar(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';

    const schemas = [
      {
        id: 'jsonld-home-organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Usine à Leads',
          url: siteUrl,
          logo: 'https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/62fb907743b5e07417bde9cd3a46f134.png',
          description: "Transformez vos visiteurs en rendez-vous qualifiés avec l'Usine à Leads. Acquisition, conversion et automatisation pour générer des leads qualifiés en continu.",
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+33-1-23-45-67-89',
            contactType: 'Customer Service',
            email: 'contact@usineleads.com',
            areaServed: 'FR',
            availableLanguage: 'French',
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Paris',
            addressCountry: 'FR',
          },
          sameAs: [
            'https://www.linkedin.com/company/usineleads',
            'https://www.facebook.com/usineleads',
            'https://twitter.com/usineleads',
          ],
        },
      },
      {
        id: 'jsonld-home-service',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Génération de Leads Qualifiés',
          provider: {
            '@type': 'Organization',
            name: 'Usine à Leads',
          },
          areaServed: {
            '@type': 'Country',
            name: 'France',
          },
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Services de Génération de Leads',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Acquisition de Leads',
                  description: 'Attirez les bons prospects avec des leviers d\'acquisition performants',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Transformation et Conversion',
                  description: 'Convertissez vos visiteurs en leads qualifiés avec des tunnels optimisés',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Automatisation et Suivi',
                  description: 'Relancez et qualifiez automatiquement vos prospects via WhatsApp et email',
                },
              },
            ],
          },
        },
      },
      {
        id: 'jsonld-home-website',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Usine à Leads',
          url: siteUrl,
          inLanguage: 'fr-FR',
          description: "Transformez vos visiteurs en rendez-vous qualifiés avec l'Usine à Leads.",
          publisher: {
            '@type': 'Organization',
            name: 'Usine à Leads',
            url: siteUrl,
          },
        },
      },
      {
        id: 'jsonld-home-webpage',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Génération de Leads Qualifiés | Usine à Leads',
          description: "Transformez vos visiteurs en rendez-vous qualifiés avec l'Usine à Leads. Acquisition, conversion et automatisation pour générer des leads qualifiés en continu. +47% de leads en 90 jours.",
          url: siteUrl,
          inLanguage: 'fr-FR',
          isPartOf: {
            '@type': 'WebSite',
            name: 'Usine à Leads',
            url: siteUrl,
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: siteUrl },
            ],
          },
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const s = document.getElementById(id);
        if (s) s.remove();
      });
    };
  }, []);

  const openPopup = () => {
    const btn = document.querySelector<HTMLElement>('[data-iclosed-link]');
    if (btn) btn.click();
  };

  return (
    <div className="min-h-screen bg-white">
      <title>Génération de Leads Qualifiés | Usine à Leads</title>
      <meta name="description" content="Transformez vos visiteurs en rendez-vous qualifiés avec l'Usine à Leads. Acquisition, conversion et automatisation pour générer des leads qualifiés en continu. +47% de leads en 90 jours." />
      <meta name="keywords" content="génération de leads, leads qualifiés, acquisition de clients, conversion de prospects, automatisation marketing, rendez-vous qualifiés, machine à leads" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/lp`} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/lp`} />
      <meta property="og:title" content="Génération de Leads Qualifiés | Usine à Leads" />
      <meta property="og:description" content="Transformez vos visiteurs en rendez-vous qualifiés avec l'Usine à Leads. +47% de leads en 90 jours. Acquisition, conversion et automatisation pour des résultats mesurables." />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Génération de Leads Qualifiés | Usine à Leads" />
      <meta name="twitter:description" content="Transformez vos visiteurs en rendez-vous qualifiés avec l'Usine à Leads. +47% de leads en 90 jours." />

      {/* Navbar fixe au scroll */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showNavbar ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img
                src={showNavbar
                  ? "https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/8eef813465f798e5a4134d50e400907e.png"
                  : "https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/512cce5c00ccd36955d2d4e00fd25839.png"
                }
                alt="Usine à Leads"
                className="h-12 w-auto"
              />
            </div>
            <button
              data-iclosed-link="https://app.iclosed.io/e/usinealead/rdv-45-min-votre-business-votre-besoin"
              data-embed-type="popup"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold text-sm hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Obtenir un RDV
            </button>
          </div>
        </div>
      </nav>

      <Hero onCTAClick={openPopup} />
      <RealityCheck />
      <WhatWeDo />
      <HowItWorks />
      <WhatYouGet />
      <Testimonials />
      <Pricing onCTAClick={openPopup} />
      <FAQ />
      <FinalCTA onCTAClick={openPopup} />
      <Footer />
    </div>
  );
}
